import React, { useCallback, forwardRef, Ref, KeyboardEvent } from "react"

import { IconTickMedium } from "@izettle/otto"

import { SemanticState, KeyCodes } from "../../../enumerations"

import {
  SelectOptionContainer,
  SelectOptionContent,
  SelectOptionText,
  SelectOptionDescription,
  SelectCheckbox,
  StyledInputIcon,
  StyledImage,
  FocusRemover,
  SelectRadioButton,
} from "./styles"
import { SelectOptionProps } from "./types"

export const SelectOption = forwardRef(
  (
    {
      id,
      key,
      text,
      image,
      description,
      isExpanded = false,
      isMultiple = false,
      isMobile = false,
      isSelected = false,
      onSelect,
      onOptionItemFocused,
      onNext,
      onPrevious,
      onTab,
      onEscape,
      disabled,
    }: SelectOptionProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (!isExpanded) {
          return
        }
        switch (event.which) {
          case KeyCodes.Up: {
            event.preventDefault()
            onPrevious?.()
            break
          }
          case KeyCodes.Down: {
            event.preventDefault()
            onNext?.()
            break
          }
          case KeyCodes.Enter: {
            if (disabled) break
            event.preventDefault()
            onSelect?.()
            break
          }
          case KeyCodes.Tab:
            onTab?.()
            break
          case KeyCodes.Escape:
            onEscape?.()
            break
          default:
            break
        }
      },
      [isExpanded, onPrevious, onNext, onEscape],
    )

    const renderRadioButton = useCallback(() => {
      if (isMobile) {
        return (
          <SelectRadioButton
            id={`radio-button-${id}`}
            checked={isSelected}
            disabled={disabled}
          />
        )
      }
    }, [isSelected, isMobile, disabled])

    const renderTick = useCallback(() => {
      if (isSelected && !isMobile) {
        return (
          <StyledInputIcon
            semanticState={SemanticState.Success}
            block
            svgIcon={IconTickMedium}
          />
        )
      }
    }, [isSelected, isMobile])

    return (
      <SelectOptionContainer
        id={id}
        role="option"
        aria-hidden={!isExpanded}
        aria-selected={isSelected}
        aria-checked={isSelected}
        tabIndex={key === 0 ? 0 : -1}
        onClick={() => !disabled && onSelect?.()}
        isSelected={isSelected}
        isMultiple={isMultiple}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={`${text}, ${description}`}
      >
        <FocusRemover
          tabIndex={-1}
          ref={ref}
          onFocus={onOptionItemFocused}
          onKeyDown={handleKeyDown}
        >
          {isMultiple ? (
            <>
              {image && <StyledImage src={image} aria-hidden role="presentation" />}
              <SelectCheckbox
                id={`checkbox-${id}`}
                checked={isSelected}
                disabled={disabled}
              />
              <SelectOptionContent>
                <SelectOptionText aria-hidden disabled={disabled}>
                  {text}
                </SelectOptionText>
                {description && (
                  <SelectOptionDescription aria-hidden>
                    {description}
                  </SelectOptionDescription>
                )}
              </SelectOptionContent>
            </>
          ) : (
            <>
              {renderRadioButton()}
              {image && (
                <StyledImage src={image} aria-hidden="true" role="presentation" />
              )}
              <SelectOptionContent>
                <SelectOptionText disabled={disabled} aria-hidden>
                  {text}
                </SelectOptionText>
                {description && (
                  <SelectOptionDescription aria-hidden>
                    {description}
                  </SelectOptionDescription>
                )}
              </SelectOptionContent>
              {renderTick()}
            </>
          )}
        </FocusRemover>
      </SelectOptionContainer>
    )
  },
)
